import { useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, CardBody, ConfirmDialog, DataTable, EmptyState, Input, LoadingState, Select, type Column } from "@agentnative/ui";
import { useAuthStore } from "../stores/auth";
import { useCurrentOrgStore } from "../stores/currentOrg";
import { apiErrorMessage } from "../lib/api/client";
import { OneTimeSecretAlert } from "../components/OneTimeSecret";
import {
  createOAuthAgent,
  getOAuthCapabilities,
  listOAuthAgents,
  listOAuthResources,
  rotateOAuthAgentSecret,
  setOAuthAgentDisabled,
  updateOAuthAgent,
  type OAuthAgent,
  type OneTimeSecret,
} from "../lib/api/oauth";

type RoleCap = OAuthAgent["role_cap"];

const ROLE_LABEL: Record<RoleCap, string> = {
  viewer: "Read-only (viewer)",
  member: "Member",
  admin: "Admin",
};

interface FormState {
  id: string | null;
  name: string;
  /** Bound organizations, each with its own scope cap. The first is the primary. */
  orgs: { orgId: string; scopes: string[] }[];
  resources: string[];
  roleCap: RoleCap;
}

const MAX_ORGS = 10;

export function AgentsRoute() {
  const user = useAuthStore((s) => s.user);
  const memberships = useCurrentOrgStore((s) => s.memberships);
  const currentOrg = useCurrentOrgStore((s) => s.selectedOrganizationId);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [secret, setSecret] = useState<OneTimeSecret | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "disable" | "rotate"; agent: OAuthAgent } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["oauth-agents"] });

  const capsQuery = useQuery({ queryKey: ["oauth-capabilities"], queryFn: getOAuthCapabilities, enabled: !!user });
  const allowed = capsQuery.data?.manage_agents === true;
  const isAdmin = capsQuery.data?.platform_admin === true;
  const agentsQuery = useQuery({ queryKey: ["oauth-agents"], queryFn: listOAuthAgents, enabled: allowed });
  const resourcesQuery = useQuery({ queryKey: ["oauth-resources"], queryFn: listOAuthResources, enabled: allowed });

  const ownedOrgs = useMemo(
    () => memberships.filter((m) => m.role === "MEMBER_ROLE_OWNER").map((m) => ({ value: m.organization.id, label: m.organization.name })),
    [memberships],
  );
  const resources = resourcesQuery.data?.resources ?? [];
  const offeredScopes = useMemo(() => {
    const out: Record<string, string> = {};
    for (const r of resources) {
      if (form?.resources.includes(r.resource_uri)) Object.assign(out, r.scopes);
    }
    return out;
  }, [resources, form?.resources]);

  const saveMutation = useMutation({
    mutationFn: async (f: FormState) => {
      const base = {
        name: f.name.trim(),
        resources: f.resources,
        role_cap: f.roleCap,
        orgs: f.orgs.map((o) => ({ organization_id: o.orgId, scopes: o.scopes.filter((s) => s in offeredScopes) })),
      };
      if (f.id) return { created: null, updated: await updateOAuthAgent(f.id, base) };
      return { created: await createOAuthAgent(base), updated: null };
    },
    onSuccess: (r) => {
      if (r.created) setSecret({ client_id: r.created.client_id, client_secret: r.created.client_secret, message: r.created.message });
      setForm(null);
      refresh();
    },
  });
  const disableMutation = useMutation({
    mutationFn: (v: { id: string; disabled: boolean }) => setOAuthAgentDisabled(v.id, v.disabled),
    onSuccess: () => {
      setConfirm(null);
      refresh();
    },
  });
  const rotateMutation = useMutation({
    mutationFn: (id: string) => rotateOAuthAgentSecret(id),
    onSuccess: (s) => {
      setConfirm(null);
      setSecret(s);
    },
  });

  if (!user) {
    return <EmptyState title="Not signed in" description="Sign in to manage agents." />;
  }
  if (capsQuery.isLoading) return <LoadingState message="Loading..." />;
  if (!allowed) {
    return (
      <EmptyState
        title="Organization owner required"
        description="Agents are managed by organization owners and platform admins."
      />
    );
  }

  const formValid =
    !!form &&
    form.name.trim() !== "" &&
    form.resources.length > 0 &&
    form.orgs.length > 0 &&
    form.orgs.every((o) => o.orgId !== "" && o.scopes.some((s) => s in offeredScopes)) &&
    new Set(form.orgs.map((o) => o.orgId)).size === form.orgs.length;

  function startCreate() {
    setForm({ id: null, name: "", orgs: [{ orgId: currentOrg ?? ownedOrgs[0]?.value ?? "", scopes: [] }], resources: [], roleCap: "viewer" });
  }
  function startEdit(a: OAuthAgent) {
    const orgs = a.orgs?.length ? a.orgs : [{ organization_id: a.organization_id, scopes: a.scopes }];
    setForm({ id: a.id, name: a.name, orgs: orgs.map((o) => ({ orgId: o.organization_id, scopes: o.scopes })), resources: a.resources, roleCap: a.role_cap });
  }
  function setOrg(i: number, patch: Partial<FormState["orgs"][number]>) {
    if (!form) return;
    setForm({ ...form, orgs: form.orgs.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  }
  function toggle(list: string[], v: string, on: boolean) {
    return on ? [...list, v] : list.filter((x) => x !== v);
  }

  const columns: Column<OAuthAgent>[] = [
    {
      key: "name",
      label: "Agent",
      render: (a) => (
        <div>
          <div className="font-medium">{a.name}</div>
          <div className="font-mono text-xs text-[var(--color-neutral-500)]">{a.client_id}</div>
        </div>
      ),
    },
    {
      key: "org",
      label: "Organizations",
      render: (a) => (
        <ul className="space-y-1 text-xs">
          {(a.orgs?.length ? a.orgs : [{ organization_id: a.organization_id, organization_name: a.organization_name, scopes: a.scopes }]).map((o, i) => (
            <li key={o.organization_id}>
              <span className="text-sm">{o.organization_name || o.organization_id}</span>
              {i === 0 && " (primary)"}
              <div className="font-mono text-[var(--color-neutral-500)]">{o.scopes.join(", ")}</div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: "access",
      label: "Access",
      render: (a) => (
        <div className="text-xs">
          <div className="break-all font-mono">{a.resources.join(", ")}</div>
        </div>
      ),
    },
    { key: "role", label: "Role cap", render: (a) => ROLE_LABEL[a.role_cap] },
    { key: "state", label: "Status", render: (a) => (a.disabled ? <Badge variant="danger">Disabled</Badge> : <Badge variant="success">Active</Badge>) },
    {
      key: "actions",
      label: "",
      render: (a) => (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => startEdit(a)} aria-label={`Edit ${a.name}`}>
            Edit
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setConfirm({ kind: "rotate", agent: a })} aria-label={`Rotate secret for ${a.name}`}>
            Rotate secret
          </Button>
          {a.disabled ? (
            <Button size="sm" variant="secondary" disabled={disableMutation.isPending} onClick={() => disableMutation.mutate({ id: a.id, disabled: false })} aria-label={`Enable ${a.name}`}>
              Enable
            </Button>
          ) : (
            <Button size="sm" variant="danger" onClick={() => setConfirm({ kind: "disable", agent: a })} aria-label={`Disable ${a.name}`}>
              Disable
            </Button>
          )}
        </div>
      ),
    },
  ];

  const mutationError = [saveMutation, disableMutation, rotateMutation].find((m) => m.isError);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Agents</h1>
        <Button onClick={startCreate} disabled={form !== null}>
          Create an agent
        </Button>
      </div>
      <p className="text-sm text-[var(--color-neutral-500)]">
        An agent is a named, automated identity (for example an ATF agent) that gets its own short-lived tokens with the
        client credentials grant. It is bound to one or more organizations (each with its own scopes), only the resources and scopes you allow, and a role cap
        in the app that is read-only unless you raise it. Tokens last 5 minutes. Disable an agent to stop it immediately.
        Everything it does is recorded under its name in the OAuth audit.
      </p>

      {secret && <OneTimeSecretAlert label="Agent" clientId={secret.client_id} secret={secret.client_secret} onDismiss={() => setSecret(null)} />}
      {mutationError && (
        <Alert variant="danger" title="Action failed">
          {apiErrorMessage(mutationError.error)}
        </Alert>
      )}

      {form && (
        <Card padding="lg">
          <CardBody>
            <h2 className="mb-3 text-lg font-semibold">{form.id ? "Edit agent" : "Create an agent"}</h2>
            <form
              className="space-y-4"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (formValid) saveMutation.mutate(form);
              }}
            >
              <Input label="Name" value={form.name} onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })} />
              <fieldset>
                <legend className="mb-1 text-sm font-medium">Resources it may use</legend>
                <p className="mb-1 text-xs text-[var(--color-neutral-500)]">
                  Only apps the organization holds an active license for can be granted; others are refused when you save.
                </p>
                {resources.map((r) => (
                  <label key={r.id} className="flex items-start gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      checked={form.resources.includes(r.resource_uri)}
                      onChange={(e) => setForm({ ...form, resources: toggle(form.resources, r.resource_uri, e.target.checked) })}
                    />
                    <span>
                      {r.name} <span className="font-mono text-xs text-[var(--color-neutral-500)]">{r.resource_uri}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <fieldset className="space-y-3">
                <legend className="mb-1 text-sm font-medium">Organizations and scopes</legend>
                <p className="text-xs text-[var(--color-neutral-500)]">
                  Each organization has its own scope set. The first organization is the primary one; apps that do not support several
                  organizations only see it. You must own every organization listed.
                </p>
                {form.orgs.map((o, i) => (
                  <div key={i} className="rounded border border-[var(--color-neutral-200)] p-3">
                    <div className="mb-2 flex items-end gap-2">
                      <div className="flex-1">
                        {isAdmin ? (
                          <Input
                            label={i === 0 ? "Primary organization ID" : "Organization ID"}
                            value={o.orgId}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setOrg(i, { orgId: e.target.value })}
                          />
                        ) : (
                          <Select
                            label={i === 0 ? "Primary organization" : "Organization"}
                            value={o.orgId}
                            options={ownedOrgs}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => setOrg(i, { orgId: e.target.value })}
                          />
                        )}
                      </div>
                      {form.orgs.length > 1 && (
                        <Button type="button" size="sm" variant="secondary" onClick={() => setForm({ ...form, orgs: form.orgs.filter((_, j) => j !== i) })} aria-label={`Remove organization ${i + 1}`}>
                          Remove
                        </Button>
                      )}
                    </div>
                    {Object.keys(offeredScopes).length === 0 ? (
                      <p className="text-xs text-[var(--color-neutral-500)]">Choose resources to see scopes.</p>
                    ) : (
                      Object.entries(offeredScopes).map(([scope, desc]) => (
                        <label key={scope} className="flex items-start gap-2 py-1 text-sm">
                          <input type="checkbox" checked={o.scopes.includes(scope)} onChange={(e) => setOrg(i, { scopes: toggle(o.scopes, scope, e.target.checked) })} />
                          <span>
                            <span className="font-mono text-xs">{scope}</span> {desc}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                ))}
                {form.orgs.length < MAX_ORGS && (
                  <Button type="button" size="sm" variant="secondary" onClick={() => setForm({ ...form, orgs: [...form.orgs, { orgId: "", scopes: [] }] })}>
                    Add an organization
                  </Button>
                )}
              </fieldset>
              <Select
                label="Role cap in the app"
                value={form.roleCap}
                helperText="The most the agent can do in the app, whatever its scopes. The app combines this with the scopes."
                options={(isAdmin ? (["viewer", "member", "admin"] as RoleCap[]) : (["viewer", "member"] as RoleCap[])).map((r) => ({ value: r, label: ROLE_LABEL[r] }))}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setForm({ ...form, roleCap: e.target.value as RoleCap })}
              />
              <div className="flex gap-2">
                <Button type="submit" disabled={!formValid || saveMutation.isPending}>
                  {form.id ? "Save changes" : "Create agent"}
                </Button>
                <Button type="button" variant="secondary" onClick={() => setForm(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {agentsQuery.isLoading ? (
        <LoadingState message="Loading agents..." />
      ) : agentsQuery.isError ? (
        <Alert variant="danger" title="Failed to load agents">
          {apiErrorMessage(agentsQuery.error)}
        </Alert>
      ) : (agentsQuery.data ?? []).length === 0 ? (
        <EmptyState title="No agents" description="Create one to give an automated workflow its own access." />
      ) : (
        <Card padding="none">
          <DataTable<OAuthAgent> data={agentsQuery.data!} columns={columns} emptyMessage="No agents." />
        </Card>
      )}

      <ConfirmDialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm?.kind === "rotate" ? "Rotate secret" : "Disable agent"}
        message={
          confirm?.kind === "rotate"
            ? `${confirm.agent.name}'s current secret stops working immediately. You will see the new one once.`
            : `${confirm?.agent.name ?? "This agent"} gets no new tokens, and tokens it already holds fail introspection at once. Any grants held by its client are revoked.`
        }
        confirmText={confirm?.kind === "rotate" ? "Rotate" : "Disable"}
        variant="danger"
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === "rotate") rotateMutation.mutate(confirm.agent.id);
          else disableMutation.mutate({ id: confirm.agent.id, disabled: true });
        }}
      />
    </div>
  );
}
